import type { QueueStorage, QueuedWrite } from './types.js'

/**
 * Durable storage for the write queue, in IndexedDB.
 *
 * `OFF-001` requires the queue to survive "a dropped connection, a page reload and a **browser
 * crash**". `localStorage` would satisfy the first two and is synchronous, size-limited and
 * string-only; a queued sale carries a body, and a shop on a cheap tablet will hit the limit before
 * anybody notices it exists. IndexedDB is the one browser store built for this.
 *
 * `OFF-001c` later replaces the whole thing with a local database and bidirectional sync in the
 * desktop client. It replaces this file, not the queue above it, which is what `OFF-001a` bought.
 */

const STORE = 'writes'

export class IndexedDbQueueStorage implements QueueStorage {
  readonly #databaseName: string
  #db: IDBDatabase | undefined

  /**
   * The database name carries the tenant, and that is not decoration.
   *
   * Two tenants on one shared tablet, which happens where an owner runs two shops, must not share a
   * queue: a pending write for one would be sent under the other's session. Physical separation is
   * the tenancy rule everywhere else in this product (`TEN-002`), and there is no reason for the
   * client to be the exception.
   */
  constructor(tenantId: string) {
    this.#databaseName = `lensmira-writes-${tenantId}`
  }

  async #open(): Promise<IDBDatabase> {
    if (this.#db) return this.#db
    this.#db = await new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(this.#databaseName, 1)
      request.onupgradeneeded = () => {
        const db = request.result
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' })
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error ?? new Error('indexedDB.open failed'))
    })
    return this.#db
  }

  async #transact<T>(
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const db = await this.#open()
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode)
      const request = run(tx.objectStore(STORE))
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'))
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'))
    })
  }

  async all(): Promise<QueuedWrite[]> {
    const rows = await this.#transact<QueuedWrite[]>(
      'readonly',
      (store) => store.getAll() as IDBRequest<QueuedWrite[]>,
    )
    return rows
  }

  async put(write: QueuedWrite): Promise<void> {
    await this.#transact('readwrite', (store) => store.put(write))
  }

  async remove(id: string): Promise<void> {
    await this.#transact('readwrite', (store) => store.delete(id))
  }

  async clear(): Promise<void> {
    await this.#transact('readwrite', (store) => store.clear())
  }
}
